import * as ToastPrimitive from "@radix-ui/react-toast";
import { CircleCheck, CircleX, Info, X } from "lucide-react";
import { forwardRef, useEffect } from "react";
import { create } from "zustand";
import { cn } from "@/lib/cn";

type ToastVariant = "success" | "error" | "info";
const TOAST_AUTO_DISMISS_MS = 5000;

interface ToastData {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
}

let toastCounter = 0;

const useToastStore = create<{
  toasts: ToastData[];
  add: (t: Omit<ToastData, "id">) => void;
  remove: (id: string) => void;
}>((set) => ({
  toasts: [],
  add: (t) => {
    const id = `toast-${++toastCounter}`;
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
  },
  remove: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

function toast(data: Omit<ToastData, "id">) {
  useToastStore.getState().add(data);
}

function useToast() {
  return useToastStore((s) => s.toasts);
}
const variantIcon: Record<ToastVariant, React.ReactNode> = {
  success: <CircleCheck className="size-5 shrink-0 text-lime" />,
  error: <CircleX className="size-5 shrink-0 text-danger" />,
  info: <Info className="size-5 shrink-0 text-electric" />,
};

const ToastViewport = forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    data-slotique-toast-viewport
    className={cn(
      "fixed bottom-[var(--toast-viewport-bottom,calc(1rem+env(safe-area-inset-bottom)))] right-4 z-50 flex w-[380px] max-w-[calc(100vw-2rem)] flex-col gap-2",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = "ToastViewport";
const ToastRoot = forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root> & { variant?: ToastVariant }
>(({ className, variant = "info", children, ...props }, ref) => (
  <ToastPrimitive.Root
    ref={ref}
    className={cn(
      "bg-ink text-white rounded-sm shadow-pop px-4 py-3 flex items-start gap-3",
      "data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-full",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full",
      "data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]",
      "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
      className,
    )}
    {...props}
  >
    {variantIcon[variant]}
    {children}
  </ToastPrimitive.Root>
));
ToastRoot.displayName = "ToastRoot";
const ToastTitle = forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={cn("text-[14px] font-semibold", className)}
    {...props}
  />
));
ToastTitle.displayName = "ToastTitle";
const ToastDescription = forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn("text-[13px] text-white/80", className)}
    {...props}
  />
));
ToastDescription.displayName = "ToastDescription";
const ToastClose = forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    className={cn(
      "shrink-0 rounded-xs p-1 text-white/60 hover:text-white transition-colors -mt-0.5 -mr-1",
      className,
    )}
    {...props}
  >
    <X className="size-4" />
  </ToastPrimitive.Close>
));
ToastClose.displayName = "ToastClose";

function ToastItem({ toast: t, onRemove }: { toast: ToastData; onRemove: (id: string) => void }) {
  useEffect(() => {
    const timeout = window.setTimeout(() => onRemove(t.id), TOAST_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timeout);
  }, [t.id, onRemove]);

  return (
    <ToastRoot variant={t.variant} onOpenChange={(open) => !open && onRemove(t.id)}>
      <div className="min-w-0 flex-1">
        <ToastTitle>{t.title}</ToastTitle>
        {t.description && <ToastDescription>{t.description}</ToastDescription>}
      </div>
      <ToastClose />
    </ToastRoot>
  );
}

function Toaster() {
  const toasts = useToast();
  const remove = useToastStore((s) => s.remove);

  return (
    <ToastPrimitive.Provider duration={TOAST_AUTO_DISMISS_MS}>
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onRemove={remove} />
      ))}
      <ToastViewport />
    </ToastPrimitive.Provider>
  );
}

export { Toaster, toast, useToastStore };
